import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import _ from 'lodash';
import { BehaviorSubject, fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Label } from 'src/app/labels/englishLabels';

import { FilterTypesEnum } from 'src/app/models/filters/filterTypes.enum';
import { IPageFilters } from 'src/app/models/filters/interfaces/pageFilters.interface';
import { ISelectOption } from 'src/app/models/selectOptions';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-autocomplete-api',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatInputModule,
    MatFormFieldModule,
  ],
  templateUrl: './autocomplete-api.component.html',
  styleUrls: ['./autocomplete-api.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutocompleteApiComponent implements OnInit, AfterViewInit {
  @Input() public pageFilters: IPageFilters;
  @Input() public searchValuesCtrl = new UntypedFormControl();
  @Input() public filteredValues: ISelectOption[];
  @Input() public isLoading$: BehaviorSubject<boolean>;
  @Input() public filterType: FilterTypesEnum;
  @Input() public maxSelectedItems?: number;

  @Output() public optionOnSelection = new EventEmitter<ISelectOption[]>();

  @ViewChild('autocompleteApi') private _autocompleteApiElement?: ElementRef<HTMLDivElement>;

  public allValues$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);
  public selectedIds: Set<string>;
  public maxSelectedItemMessage: string;
  public panelWidth = '0px';
  private _resizeObserver?: ResizeObserver;

  constructor(
    private readonly _cdr: ChangeDetectorRef,
    private readonly _destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    this.pageFilters
      .getAllFiltersOptions()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((options) => {
        const previouslySelectedOptions: ISelectOption[] = this.allValues$.value;
        options.forEach((option) => {
          if (option.categoryName === this.filterType && !this.allValues$.value.some((v) => v.id === option.id)) {
            previouslySelectedOptions.push(option);
          }
        });
        this.allValues$.next(previouslySelectedOptions);
      });

    this.pageFilters.onUpdate$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      const newSelectedIds: Set<string> = new Set();
      this.pageFilters.getAllSelectedOptions().forEach((option) => {
        if (option.categoryName === this.filterType) {
          newSelectedIds.add(option.id);
        }
      });
      this.selectedIds = newSelectedIds;
    });

    if (this.maxSelectedItems !== undefined) {
      this.maxSelectedItemMessage = Label.get('common.maximumItemsSelectionReached').replace(
        '{0}',
        this.maxSelectedItems
      );
    }
  }

  ngAfterViewInit(): void {
    this._setupResizeObserver();
    this._updatePanelWidth();
    this._cdr.detectChanges();

    fromEvent(window, 'resize')
      .pipe(debounceTime(100), takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this._updatePanelWidth());
  }

  public displayWith(value: ISelectOption): string {
    return value === null ? value : value.displayName;
  }

  public optionClicked(event: Event, value: ISelectOption) {
    if (this.isOptionDisabled(value)) {
      event.stopPropagation();
      return;
    }
    this.toggleSelection(value);
    event.stopPropagation();
  }

  public toggleSelection(selectedOption: ISelectOption) {
    const selectedOptionIsNew = !this.allValues$.value.some((option) => option.id === selectedOption.id);
    const _isMaxItemsReached = this.isMaxReached();

    if (!this.selectedIds.has(selectedOption.id)) {
      if (!_isMaxItemsReached) {
        this.selectedIds.add(selectedOption.id);
      }
    } else {
      this.selectedIds.delete(selectedOption.id);
      this.clearSingleOption(selectedOption);
    }

    const allOptions =
      selectedOptionIsNew && !_isMaxItemsReached
        ? _.union([selectedOption], this.allValues$.value)
        : this.allValues$.value;
    this.allValues$.next(allOptions);

    this.searchValuesCtrl.setValue(this.selectedIds);
    this.optionOnSelection.emit(allOptions.filter((option) => this.selectedIds.has(option.id)));
  }

  public clearSingleOption(optionToRemove: ISelectOption) {
    this.pageFilters.removeSelectedOptionsIfExist([optionToRemove]);
  }

  public isOptionDisabled(value: ISelectOption): boolean {
    return (
      this.maxSelectedItems !== undefined &&
      this.maxSelectedItems === this.selectedIds.size &&
      !this.selectedIds.has(value.id)
    );
  }

  public isMaxReached(): boolean {
    return this.maxSelectedItems !== undefined && this.selectedIds.size === this.maxSelectedItems;
  }

  public getTooltipIfTruncated(text: string): string {
    const TRUNCATION_THRESHOLD = 30;
    return text && text.length > TRUNCATION_THRESHOLD ? text : '';
  }

  private _updatePanelWidth(): void {
    const hostElement = this._autocompleteApiElement?.nativeElement;

    if (!hostElement) {
      return;
    }

    const newWidth = `${hostElement.clientWidth}px`;

    if (this.panelWidth !== newWidth) {
      this.panelWidth = newWidth;
      this._cdr.markForCheck();
    }
  }

  private _setupResizeObserver(): void {
    if (this._resizeObserver || typeof ResizeObserver === 'undefined') {
      return;
    }

    const hostElement = this._autocompleteApiElement?.nativeElement;

    if (!hostElement) {
      return;
    }

    this._resizeObserver = new ResizeObserver(() => this._updatePanelWidth());
    this._resizeObserver.observe(hostElement);
    this._destroyRef.onDestroy(() => this._resizeObserver?.disconnect());
  }
}
